import { useState, useEffect, useCallback } from 'react';
import { api } from '../services/api';

const BADGE_REFRESH_EVENT = 'nuance:refresh-badges';

/**
 * Trigger a badge refresh from anywhere in the app.
 * Call this after actions that change badge counts:
 * source created/deactivated, file uploaded, pipeline completed, etc.
 */
export function refreshNavBadges() {
  window.dispatchEvent(new CustomEvent(BADGE_REFRESH_EVENT));
}

/**
 * Fetches action-needed badge counts from `/api/manage/badges`.
 * Event-driven — fetches on mount and when refreshNavBadges() is called.
 * No polling interval.
 */
export function useNavBadges() {
  const [badges, setBadges] = useState<Record<string, number>>({});

  const fetchBadges = useCallback(async () => {
    try {
      const data = await api.get<{ badges: Record<string, number> }>('/manage/badges');
      setBadges(data.badges ?? {});
    } catch {
      // Non-critical — don't surface badge fetch failures
    }
  }, []);

  useEffect(() => {
    // Fetch once on mount
    fetchBadges();

    // Listen for refresh events from other components
    const handler = () => fetchBadges();
    window.addEventListener(BADGE_REFRESH_EVENT, handler);
    return () => window.removeEventListener(BADGE_REFRESH_EVENT, handler);
  }, [fetchBadges]);

  return badges;
}
